import React from "react"
import Layout from "../../components/layout"
import SubNavigation from "../../components/sub-navigation"
import LendingMap from "../../components/lending-map"
import ReadNext from "../../components/read-next"
import { Appendix } from "../../components/appendix"
import { Link, withPrefix } from "gatsby"

const PAGESLUG = "lending"

export default function Lending() {
  return (
    <Layout>
      <SubNavigation id="What We Do" current={PAGESLUG} />
      <section className="page__section">
        <div className="page__section__inner">
          <div className="container container--large">
            <h1 className="page__title--primary">Lending</h1>
            <h2 className="page__title--opener">
              $165 billion to 83 countries, including $16.1 billion to 49
              low-income countries
            </h2>
            <p>
              About $91 billion in financing to 80 countries, including $11.3
              billion to 48 low-income countries since the onset of the pandemic
              in late March and as of September 15, 2020.
            </p>
            <p>
              Out of this lending*, the IMF channeled $30 billion (equivalent to
              SDR 21.2 billion) in financing to 69 countries through emergency
              lending facilities.
            </p>
            <p>
              The IMF provides financing to member countries experiencing
              actual, potential, or prospective balance of payments problems to
              help them rebuild their international reserves and restore
              conditions for strong economic growth, while correcting underlying
              problems. The IMF also provides emergency financing and has
              massively stepped up such financing to help member countries
              address the immediate impact of the COVID-19 pandemic.
            </p>
            <hr />
            <p className="page__meta">
              *Lending information covers May 1, 2019–September 15, 2020.
            </p>
          </div>
        </div>
      </section>
      <LendingMap />
      <section className="page__section">
        <div className="page__section__inner">
          <div className="container container--large">
            <p>
              Unlike development banks, the IMF does not lend for specific
              projects. Instead, IMF financing is meant to help member countries
              tackle balance of payments problems, stabilize their economies,
              and restore sustainable economic growth. IMF financing can also be
              provided in response to natural disasters or pandemics. Finally,
              the IMF also provides precautionary financing to help prevent and
              insure against future crises and continues to enhance the tools
              available for crisis prevention.
            </p>
            <p>
              In broad terms, the IMF has two types of lending—loans provided at
              nonconcessional interest rates and loans provided to low-income
              countries on concessional terms. Currently, concessional loans do
              not bear any interest.
            </p>
            <p>
              The IMF has responded to the COVID-19 pandemic with unprecedented
              speed and magnitude, making use of its current $1 trillion lending
              capacity.¹
            </p>
            <p>
              This response has entailed provision of financial assistance to
              countries with urgent or potential balance of payments needs with
              the aim to help protect the lives and livelihoods of people,
              especially the most vulnerable. Between the onset of the pandemic
              in late March and September 15, 2020, about $91 billion (or SDR 64
              billion) has been committed to 80 member countries, of which $30
              billion was emergency financing (RCF and RFI). On the lending
              front, actions have focused on five tracks:
            </p>
            <ol className="page__list page__list--ordered page__list--ordered--light">
              <li>
                <h3 className="title--quaternary">
                  Emergency financing under the RFI and RCF
                </h3>
                <p>
                  The IMF is responding to a record number of requests for
                  emergency financing—from 69 countries as of September 15,
                  2020. The Board temporarily doubled the access limits to the
                  emergency facilities—
                  <a href="https://www.imf.org/en/About/Factsheets/Sheets/2016/08/02/21/08/Rapid-Credit-Facility">
                    the Rapid Credit Facility (RCF)
                  </a>{" "}
                  and{" "}
                  <a href="https://www.imf.org/en/About/Factsheets/Sheets/2016/08/02/19/55/Rapid-Financing-Instrument">
                    Rapid Financing Instrument (RFI)
                  </a>
                  —allowing it to meet the expected immediate demand from member
                  countries (see RFI and RCF agreement details on Access Limits
                  in the <Link to="#lending-map">interactive lending map</Link>{" "}
                  above). These facilities allow the IMF to provide emergency
                  assistance without the need for the member to have a
                  full-fledged program in place.
                </p>
              </li>
              <li>
                <h3 className="title--quaternary">
                  Augmenting existing lending arrangements
                </h3>
                <p>
                  The IMF is also augmenting existing lending programs to
                  accommodate urgent new needs arising from the coronavirus,
                  thereby enabling a response to the COVID-19 crisis within the
                  context of the ongoing policy dialogue. Augmentation requests
                  have been approved by the Board for eight countries as of
                  September 15, 2020.
                </p>
              </li>
              <li>
                <h3 className="title--quaternary">
                  New lending arrangements, including precautionary arrangements
                </h3>
                <p>
                  The Board approved six new IMF-supported programs to five
                  countries to mitigate the economic and social impact of the
                  crisis while maintaining macroeconomic stability. In addition,{" "}
                  <a href="https://www.imf.org/en/About/Factsheets/Sheets/2016/08/01/20/40/Flexible-Credit-Line">
                    Flexible Credit Lines (FCLs)
                  </a>{" "}
                  were made available for three countries with very strong
                  policy frameworks and track records in economic performance.
                  (In all three cases, the authorities intend to treat their
                  respective arrangements as precautionary.)
                </p>
              </li>
              <li>
                <h3 className="title--quaternary">Enhancing liquidity</h3>
                <p>
                  The Board approved the establishment of a{" "}
                  <a href="https://www.imf.org/en/About/Factsheets/Sheets/2020/04/17/short-term-liquidity-line#:~:text=As%20demand%20for%20liquidity%20has,spilling%20over%20to%20other%20countries">
                    Short-Term Liquidity Line (SLL)
                  </a>{" "}
                  to further strengthen the global financial safety net (see{" "}
                  <a href={withPrefix("/") + "downloads/table2-1.pdf"}>
                    Table 2.1
                  </a>{" "}
                  on Financial Terms Under IMF General Resources Account
                  Credit). The facility is a revolving and renewable backstop
                  for member countries with very strong policies and
                  fundamentals in need of short-term moderate balance of
                  payments support.
                </p>
              </li>
              <li>
                <h3 className="title--quaternary">Debt relief</h3>
                <p>
                  In March 2020, the{" "}
                  <a href="https://www.imf.org/en/About/Factsheets/Sheets/2016/08/01/16/49/Catastrophe-Containment-and-Relief-Trust">
                    Catastrophe Containment and Relief Trust (CCRT)
                  </a>{" "}
                  was enhanced to provide debt relief on a grant basis to the
                  IMF’s poorest members affected by the COVID-19 pandemic.
                  Twenty-nine eligible countries² have received debt service
                  relief of SDR 344 million in two six-month tranches, which
                  were approved by the Executive Board on April 13, 2020 and
                  October 2, 2020, respectively.
                </p>
              </li>
            </ol>
            <p>
              The Board has also temporarily streamlined the internal processes
              to allow the IMF to respond more quickly to members’ requests for
              emergency assistance during the COVID-19 pandemic, and in many
              cases the IMF made financing available within weeks of a request
              for emergency financing. In addition, the Board has also
              temporarily suspended the application of high-access procedures
              for RCF requests.³
            </p>
            <p>
              Policy safeguards were introduced in August 2020 to help mitigate
              financial risks from a member having high levels of combined high
              access from both the Poverty Reduction and Growth Trust and
              General Resources Account lending facilities. Under the new
              policy, safeguards apply to any Fund member with combined access
              to GRA and PRGT resources that exceeds quota-based thresholds set
              at the same level that triggers the exceptional access framework
              of the GRA.
            </p>
            <hr />
            <ol className="page__meta page__meta--footnotes">
              <li>
                Including pre-pandemic commitments, as of September 15, 2020,
                total undisbursed lending commitments and credit outstanding
                under the IMF’s GRA lending were about SDR 174.9 billion, while
                the corresponding total under the Poverty Reduction and Growth
                Trust, which provides concessional lending to low-income
                countries, was about SDR 14.4 billion.
              </li>
              <li>
                Chad is one of 29 countries approved to benefit from the CCRT
                debt service relief. However, the first tranche of CCRT relief
                covers debt service falling due during April 13-October 13,
                2020, during which Chad does not have any debt service to the
                IMF falling due. Hence, no CCRT relief was disbursed to Chad
                during this period.
              </li>
              <li>
                High-access procedures require an informal Board session based
                on a short staff note that includes discussion of program
                strength, capacity to repay, and debt vulnerabilities. The
                high-access procedures are triggered when (1) a request for Fund
                financing brings total access to more than 180 percent of quota
                over a 36-month period or (2) total outstanding credit from the
                PRGT exceeds or is projected to exceed 225 percent of quota.
              </li>
            </ol>
          </div>
        </div>
      </section>
      <ReadNext sectionID="what-we-do" pageID="capacity-development" />
      <Appendix page={PAGESLUG} />
    </Layout>
  )
}
